import React, { useState, useEffect } from 'react';
import { Box, Button, Snackbar, Alert, Typography } from '@mui/material';
import { useLocation, useNavigate, useParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import axios from 'axios';
import config from '../../config';
import { VehicleAddDetails } from './VehicleAddDetails';
import { VehicleDocumentManager } from './VehicleDocumentManager';
import SubTitle from '../common/SubTitle';

export default function VehicleAddMain() {
  const { t } = useTranslation();
  const [vehicleFormData, setVehicleFormData] = useState({
    device_id: '',
    name: '',
    VehicleNumber: '',
    description: '',
  });
  const [selectedImage, setSelectedImage] = useState(null);
  const [documents, setDocuments] = useState([]); // Store filtered documents
  const [routes, setRoutes] = useState([]);
  const [sites, setSites] = useState([]);
  const [drivers, setDrivers] = useState([]);
  const [selectedSchedules, setSelectedSchedules] = useState({});
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isScheduleModalOpen, setIsScheduleModalOpen] = useState(false);
  const [orgId, setOrgId] = useState(null);
  const [errors, setErrors] = useState({});
  const [createdVehicleId, setCreatedVehicleId] = useState(null);
  const [alert, setAlert] = useState({ open: false, message: '', severity: 'info' });
  const [isDetailsFormValid, setIsDetailsFormValid] = useState(false);
  const [detailErrors, setDetailErrors] = useState({});

  const location = useLocation();
  const navigate = useNavigate();
  const { id } = useParams();

  const isEditing = location.state ? true : false;
  const vehicleId = location.state || id || createdVehicleId || null;
  const path = id || vehicleId ? 'Vehicles > Update Vehicle' : 'Vehicles > Create Vehicle';

  const handleValidationChange = (isValid, errors) => {
    setIsDetailsFormValid(isValid);
    setDetailErrors(errors);
    console.log('VehicleAddMain Validation Change:', { isValid, errors });
  };

  const fetchRoutes = async (organizationId) => {
    try {
      const response = await axios.get(`${config.baseURL}/route/organization`, {
        params: { organizationId },
        withCredentials: true,
      });
      console.log('Routes response:', response.data);
      if (response.data && Array.isArray(response.data)) {
        setRoutes(response.data);
      } else {
        setRoutes([]);
        setAlert({ open: true, message: t('No routes found'), severity: 'info' });
      }
    } catch (error) {
      console.error('Error fetching routes:', error);
      setAlert({ open: true, message: t('Failed to fetch routes'), severity: 'error' });
      setRoutes([]);
    }
  };

  const fetchSites = async (organizationId) => {
    try {
      const response = await axios.get(`${config.baseURL}/site/organization`, {
        params: { organizationId },
        withCredentials: true,
      });
      console.log('Sites response:', response.data);
      if (response.data && Array.isArray(response.data)) {
        setSites(response.data);
      } else {
        setSites([]);
        setAlert({ open: true, message: t('No sites found'), severity: 'info' });
      }
    } catch (error) {
      console.error('Error fetching sites:', error);
      setAlert({ open: true, message: t('Failed to fetch sites'), severity: 'error' });
      setSites([]);
    }
  };

  const fetchDrivers = async (organizationId) => {
    try {
      const response = await axios.get(`${config.baseURL}/driver/organization`, {
        params: { organizationId },
        withCredentials: true,
      });
      console.log('Drivers response:', response.data);
      if (response.data && Array.isArray(response.data)) {
        setDrivers(response.data);
      } else {
        setDrivers([]);
        setAlert({ open: true, message: t('No drivers found'), severity: 'info' });
      }
    } catch (error) {
      console.error('Error fetching drivers:', error);
      setAlert({ open: true, message: t('Failed to fetch drivers'), severity: 'error' });
      setDrivers([]);
    }
  };

  useEffect(() => {
    let isMounted = true;
    const fetchData = async () => {
      try {
        const userStateString = localStorage.getItem('userState');
        if (!userStateString) {
          setErrors({ general: 'User state not found in localStorage' });
          return;
        }
        const userState = JSON.parse(userStateString);
        console.log('User state:', userState);
        
        const organizationId = userState.user.organizationId;
        if (!organizationId) {
          setErrors({ general: 'Organization ID not found' });
          return;
        }
        setOrgId(organizationId);
        console.log('Organization ID:', organizationId);

        if (isMounted) {
          await fetchRoutes(organizationId);
          await fetchSites(organizationId);
          await fetchDrivers(organizationId);
        }

        if (isEditing && vehicleId && isMounted) {
          await fetchVehicleData(vehicleId, organizationId);
        }
      } catch (error) {
        if (isMounted) {
          console.error('Error initializing data:', error);
          setErrors({ general: 'Failed to load initial data' });
        }
      }
    };

    fetchData();
    return () => {
      isMounted = false;
    };
  }, [isEditing, vehicleId]);

  const filterMostRecentDocuments = (docs) => {
    // Group documents by name
    const groupedDocs = docs.reduce((acc, doc) => {
      if (!acc[doc.name]) {
        acc[doc.name] = [];
      }
      acc[doc.name].push(doc);
      return acc;
    }, {});

    // For each group, sort by createdAt (descending) and take the most recent
    const filteredDocs = Object.values(groupedDocs).map(group => {
      return group.sort((a, b) => new Date(b.createdAt) - new Date(a.createdAt))[0];
    });

    return filteredDocs;
  };

  const fetchVehicleData = async (id, organizationId) => {
    try {
      setIsSubmitting(true);
      const vehicleResponse = await axios.get(`${config.baseURL}/vehicle/id`, {
        params: { vehicleId: id, organizationId },
        withCredentials: true,
      });
      console.log('Vehicle data response:', vehicleResponse.data);
      if (vehicleResponse.data && vehicleResponse.data.vehicle) {
        const vehicleData = vehicleResponse.data.vehicle;
        setVehicleFormData({
          ...vehicleData,
          routes: vehicleData.routeId || '',
          driverId: vehicleData.driver ? vehicleData.driver.id : '',
          sites: vehicleData.siteId || '',
        });

        // Filter documents to keep only the most recent for each type
        const allDocuments = vehicleResponse.data.vehicle.documents || [];
        const filteredDocuments = filterMostRecentDocuments(allDocuments);

        // Extract the most recent VehicleImage
        const vehicleImage = filteredDocuments.find(doc => doc.name === 'VehicleImage');
        const otherDocuments = filteredDocuments.filter(doc => doc.name !== 'VehicleImage');

        // Set the profile image
        if (vehicleImage) {
          setSelectedImage({
            location: `${config.baseURL}/upload/image?fileName=${encodeURIComponent(vehicleImage.location)}`,
            isExisting: true,
            fileName: vehicleImage.location,
            type: 'profile',
            id: vehicleImage.id,
          });
        } else {
          setSelectedImage(null);
        }

        // Set the remaining documents for VehicleDocumentManager
        setDocuments(otherDocuments);
      }
    } catch (error) {
      console.error('Error fetching vehicle data:', error);
      setAlert({ open: true, message: t('Failed to load vehicle data'), severity: 'error' });
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleSchedulesSelected = async (scheduleIds, vId) => {
    try {
      await axios.patch(`${config.baseURL}/vehicle/schedule`, {
        scheduleIds: Object.keys(scheduleIds),
        vehicleId: vId,
      });
    } catch (error) {
      console.error('Error updating schedules:', error);
      throw new Error('Failed to update schedules');
    }
  };

  const handleRoutesSelected = async (vId) => {
    const selectedRouteId = vehicleFormData.routes;
    if (!selectedRouteId) return;
    try {
      await axios.patch(`${config.baseURL}/vehicle/route`, {
        routeId: selectedRouteId,
        vehicleId: vId,
      });
    } catch (error) {
      console.error('Error updating route:', error);
      throw new Error('Failed to update route');
    }
  };

  const formatDateToISO = (dateString) => {
    if (!dateString) return '';
    if (dateString.includes('T')) return dateString;
    return `${dateString}T00:00:00.000Z`;
  };

  const validateForm = () => {
    const newErrors = {};
    const requiredFields = ['device_id', 'name', 'VehicleNumber', 'description'];
    requiredFields.forEach((field) => {
      if (!vehicleFormData[field] || (typeof vehicleFormData[field] === 'string' && vehicleFormData[field].trim().length === 0)) {
        newErrors[field] = `${field === 'VehicleNumber' ? 'Vehicle Number' : field.replace(/([A-Z])/g, ' $1').replace(/^./, str => str.toUpperCase())
          } is required`;
      }
    });
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmitVehicleDetails = async () => {
    if (!isDetailsFormValid) {
      setAlert({
        open: true,
        message: t('Please correct all validation errors before submitting'),
        severity: 'error',
      });
      return false;
    }

    setErrors(prev => ({ ...prev, ...detailErrors }));

    if (!validateForm()) {
      setAlert({ open: true, message: t('Please fill in all required fields'), severity: 'error' });
      return false;
    }

    setIsSubmitting(true);

    try {
      const vehiclePayload = {
        ...vehicleFormData,
        reRegistration: formatDateToISO(vehicleFormData.reregistrationDueDate),
        serviceDueDate: formatDateToISO(vehicleFormData.serviceDueDate),
        insurancePolicyExpiry: formatDateToISO(vehicleFormData.insurancePolicyExpiry),
        siteId: vehicleFormData.sites || null,
        routeId: vehicleFormData.routes || null,
      };

      if (!vehiclePayload.organizationId) {
        vehiclePayload.organizationId = orgId;
      }

      let response;
      if (isEditing) {
        console.log('Updating vehicle with ID:', vehicleId);
        vehiclePayload.id = vehicleId;
        response = await axios.put(
          `${config.baseURL}/vehicle`,
          vehiclePayload,
          { withCredentials: true }
        );
      } else {
        console.log('Creating new vehicle');
        response = await axios.post(
          `${config.baseURL}/vehicle`,
          vehiclePayload,
          { withCredentials: true }
        );
      }

      const newVehicleId = isEditing ? vehicleId : response.data.vehicle.id;
      console.log('Vehicle ID after submit:', newVehicleId);
      setCreatedVehicleId(newVehicleId);

      if (selectedImage && selectedImage instanceof File) {
        const imageFormData = new FormData();
        imageFormData.append('image', selectedImage);
        try {
          const response = await axios.post(
            `${config.baseURL}/upload/image?name=${encodeURIComponent('VehicleImage')}&associateEntityId=${newVehicleId}&associatedWith=Vehicle&type=profile`,
            imageFormData,
            { withCredentials: true, headers: { 'Content-Type': 'multipart/form-data' } }
          );
          setSelectedImage({
            location: `${config.baseURL}/upload/image?fileName=${encodeURIComponent(response.data.location)}`,
            isExisting: true,
            fileName: response.data.location,
            type: 'profile',
            id: response.data.id,
          });
        } catch (imageError) {
          console.error('Vehicle image upload failed:', imageError);
          setAlert({ open: true, message: t('Vehicle saved, but image upload failed'), severity: 'warning' });
        }
      }

      if (vehicleFormData.routes) {
        await handleRoutesSelected(newVehicleId);
      }

      if (Object.keys(selectedSchedules).length > 0) {
        await handleSchedulesSelected(selectedSchedules, newVehicleId);
      }

      // Refresh documents after submission
      await fetchVehicleData(newVehicleId, orgId);

      setAlert({ open: true, message: t('Vehicle and image saved successfully'), severity: 'success' });
      return newVehicleId;
    } catch (error) {
      console.error('Submission failed:', error);
      console.error('Response data:', error.response ? error.response.data : 'No response data');
      setAlert({ open: true, message: t('Failed to save vehicle or image. Please try again.'), severity: 'error' });
      return false;
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleCancel = () => {
    navigate('/Vehicle');
  };

  const handleDocumentsComplete = () => {
    navigate('/Vehicle');
    setAlert({ open: true, message: t('Vehicle, image, and documents saved successfully'), severity: 'success' });
  };

  return (
    <>
      <SubTitle title="Vehicles" path={path} />

      <Box sx={{ mb: 4 }}>
        <VehicleAddDetails
          vehicleFormData={vehicleFormData}
          setVehicleFormData={setVehicleFormData}
          selectedImage={selectedImage}
          setSelectedImage={setSelectedImage}
          routes={routes}
          drivers={drivers}
          sites={sites}
          isEditing={isEditing}
          selectedSchedules={selectedSchedules}
          setSelectedSchedules={setSelectedSchedules}
          errors={detailErrors}
          setErrors={setDetailErrors}
          onValidationChange={handleValidationChange}
        />
      </Box>

      <Box sx={{ mb: 4 }}>
        <VehicleDocumentManager
          errors={errors}
          vehicleId={isEditing ? vehicleId : createdVehicleId}
          documents={documents}
          setDocuments={setDocuments}
          isEditing={isEditing}
          onComplete={handleDocumentsComplete}
          orgId={orgId}
          onSubmit={handleSubmitVehicleDetails}
          onCancel={handleCancel}
          isDetailsFormValid={isDetailsFormValid}
        />
      </Box>

      <Snackbar
        open={alert.open}
        autoHideDuration={6000}
        onClose={() => setAlert({ ...alert, open: false })}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      >
        <Alert
          onClose={() => setAlert({ ...alert, open: false })}
          severity={alert.severity}
          sx={{ width: '100%' }}
        >
          {alert.message}
        </Alert>
      </Snackbar>
    </>
  );
}